import {
  ComponentType,
  createContext,
  useContext,
  useRef,
  useState,
} from "react";

type DialogPropsType = {
  buttonClick?: () => void;
  secondButtonClick?: () => void;
  [key: string]: any;
};

interface DialogContextProps {
  isOpen: boolean;
  dialogComponent: ComponentType<any> | null;
  dialogProps: DialogPropsType;
  containerRef: React.RefObject<HTMLDivElement>;
  dialogPosition: React.RefObject<{ x: number; y: number }>;
  openDialog: (
    dialogComponent: ComponentType<any>,
    props?: DialogPropsType
  ) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

export const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialogContext must be used within a DialogProvider");
  }
  return context;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogPosition = useRef({ x: 0, y: 0 });

  const timeoutID = useRef<NodeJS.Timeout>();

  const [isOpen, setIsOpen] = useState(false);
  const [dialogComponent, setDialogComponent] =
    useState<ComponentType<any> | null>(null);
  const [dialogProps, setDialogProps] = useState<DialogPropsType>({});

  const openDialog = (
    component: ComponentType<any>,
    props?: DialogPropsType
  ) => {
    setIsOpen(true);
    setDialogComponent(() => component);

    if (timeoutID.current) clearTimeout(timeoutID.current);
    if (props) setDialogProps({ ...props });
  };

  const closeDialog = () => {
    setIsOpen(false);

    timeoutID.current = setTimeout(() => {
      setDialogComponent(null);
      setDialogProps({});
    }, 300);
  };

  return (
    <DialogContext.Provider
      value={{
        isOpen,
        dialogComponent,
        dialogProps,
        containerRef,
        dialogPosition,
        openDialog,
        closeDialog,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};
